"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { logo_src } from "@/lib/image_paths";
import { instagram_url, thumbtack_url } from "@/lib/site";

const nav_links = [
  { href: "/#about", label: "About" },
  { href: "/services#services", label: "Services" },
  { href: "/services#portfolio", label: "Portfolio" },
  { href: "/#testimonials", label: "Testimonials" },
  { href: "/guide", label: "Austin Bridal FAQ" },
];

function link_is_active(pathname: string, href: string) {
  if (href === "/guide") return pathname === "/guide";
  if (href === "/services#services") return pathname === "/services";
  return false;
}

export function SiteHeader() {
  const pathname = usePathname();
  const [menu_open, set_menu_open] = useState(false);

  return (
    <header className="fixed top-0 left-0 z-50 w-full bg-surface/90 shadow-[0_1px_8px_rgba(30,26,23,0.04)] backdrop-blur-xl">
      <div className="mx-auto flex h-24 max-w-7xl items-center justify-between gap-gutter px-margin lg:px-margin-lg">
        <Link className="flex shrink-0 items-center" href="/" onClick={() => set_menu_open(false)}>
          <img alt="Artistry by Isha" className="h-16 w-auto sm:h-20" src={logo_src} />
        </Link>
        <nav className="hidden items-center gap-space-lg xl:flex">
          {nav_links.map((link) => (
            <Link
              className={
                link_is_active(pathname, link.href)
                  ? "whitespace-nowrap font-label-md text-label-md font-bold text-primary transition-colors"
                  : "whitespace-nowrap font-label-md text-label-md font-medium text-on-surface-variant transition-colors hover:text-on-surface"
              }
              href={link.href}
              key={link.href}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-space-md">
          <div className="hidden items-center gap-space-xs sm:flex">
            <a
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-surface-container px-2.5 py-1 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
              href={instagram_url}
              rel="noopener"
              target="_blank"
            >
              <span className="font-label-caps text-label-caps font-bold tracking-wider text-tertiary">IG</span>
              <span className="font-body-sm text-body-sm font-normal">@artistrybyisha</span>
            </a>
            <a
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-surface-container px-2.5 py-1 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
              href={thumbtack_url}
              rel="noopener"
              target="_blank"
            >
              <span className="font-label-caps text-label-caps font-bold tracking-wider text-primary">TOP PRO</span>
              <span className="font-body-sm text-body-sm font-normal">Thumbtack Austin</span>
            </a>
          </div>
          <Link
            className="flex h-12 shrink-0 items-center justify-center whitespace-nowrap rounded bg-primary-container px-space-md font-title-md text-title-md font-semibold text-on-primary-container shadow-[0_4px_20px_-2px_rgba(30,26,23,0.06)] transition-all duration-200 hover:bg-primary hover:text-on-primary hover:shadow-[0_12px_32px_-4px_rgba(184,138,88,0.2)]"
            href="/inquire"
            onClick={() => set_menu_open(false)}
          >
            <span className="hidden sm:inline">Inquire / Request Date</span>
            <span className="sm:hidden">Inquire</span>
          </Link>
          <button
            aria-expanded={menu_open}
            aria-label={menu_open ? "Close menu" : "Open menu"}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary text-on-primary xl:hidden"
            onClick={() => set_menu_open((open) => !open)}
            type="button"
          >
            <span className="material-symbols-outlined text-[18px]">{menu_open ? "close" : "menu"}</span>
          </button>
          <div aria-hidden="true" className="hidden h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary xl:flex">
            <span className="material-symbols-outlined text-[18px] text-on-primary">person</span>
          </div>
        </div>
      </div>
      {menu_open ? (
        <nav className="flex flex-col gap-3 border-t border-surface-container-high bg-surface px-margin py-4 xl:hidden">
          {nav_links.map((link) => (
            <Link
              className="font-label-md text-label-md font-medium text-on-surface"
              href={link.href}
              key={link.href}
              onClick={() => set_menu_open(false)}
            >
              {link.label}
            </Link>
          ))}
          <a className="font-body-sm text-body-sm text-on-surface-variant" href={instagram_url} rel="noopener" target="_blank">
            Instagram @artistrybyisha
          </a>
          <a className="font-body-sm text-body-sm text-on-surface-variant" href={thumbtack_url} rel="noopener" target="_blank">
            Thumbtack Top Pro Austin
          </a>
        </nav>
      ) : null}
    </header>
  );
}
